import { useState, useEffect, useRef } from "react";
import { Globe, Check } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";

export default function LanguageSwitcher({ variant = "header" }) {
  const { lang, setLang, languages, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang) || languages[0];

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const compact = variant === "header";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-button"
        aria-label="Change language"
        className={
          compact
            ? "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-slate-100 text-slate-700 text-sm"
            : "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium"
        }
      >
        <Globe className="w-4 h-4" />
        <span className="font-medium hidden sm:inline">{current.endonym}</span>
      </button>
      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 w-64 max-h-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl z-50"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold border-b border-slate-100">
            {t("lang.picker.title")}
          </div>
          {languages.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`language-option-${l.code}`}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2 ${
                  active ? "bg-emerald-50 text-emerald-900" : "text-slate-800"
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{l.endonym}</div>
                  <div className="text-[11px] text-slate-500 truncate">{l.name}</div>
                </div>
                {active && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
              </button>
            );
          })}
          <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 leading-snug">
            {t("lang.picker.beta")}
          </div>
        </div>
      )}
    </div>
  );
}
